/**
 * Word Labyrinth — moving, the strip, and what a wrong turn costs (specs/labyrinth-mode.md §5).
 *
 * Pure functions over an immutable state, so the whole rule set — including the battery ledger,
 * which is where this mode's design actually lives — is testable in node without a Phaser scene.
 *
 * The three rules that are easy to get wrong, all of them settled here:
 *
 *  1. **EVERY step from a right word onto a wrong one costs a battery** (§5.4). Walking deeper into
 *     a wrong branch is still free — the child pays for the decision to leave the sentence, not for
 *     each cell of the consequence — but leaving it again costs again, every time.
 *
 *     This used to be charged once per turn per board, on the argument that re-entering a corridor
 *     you had already bought was a memory penalty rather than a reading one. In play it read as a
 *     bug: most wrong turns cost nothing, because most wrong turns are ones you have already tried,
 *     and a price that applies to some misreads and not others teaches nothing at all. The memory
 *     argument is answered properly by rule 4 instead.
 *
 *  2. **The charge does not bounce the player** (§5.3). They are moved onto the cell they chose.
 *     A bounce would announce "that was wrong" before they had read a word of it, and the first
 *     real signal is meant to be the strip at the bottom reading gibberish.
 *
 *  3. **Leftward movement is retracing, never advancing** (§7.3). Every step to a lower column is
 *     a step back along the trail already walked.
 *
 *  4. **Tapping ANY cell of the trail jumps back to it**, free (§5.1). Four cells down a dead end,
 *     the way out is the last right word — so touch it, rather than retracing four times. This is
 *     what makes rule 1 fair: a child is charged for every decision to leave the sentence, and
 *     never for the walk back to it.
 */
import { edgeKey, type Cell, type LabyrinthBoard } from './maze';

export interface WalkState {
  /** Cells stepped on, in order. `trail[0]` is always the entrance cell. */
  trail: Cell[];
  /** How many wrong turns this board has charged for — what `wasPerfect` reads. */
  wrongTurns: number;
  batteries: number;
  /** True once the strip reads the board's sentence — see `gateIsOpen`. */
  gateOpen: boolean;
  cleared: boolean;
}

export type MoveOutcome =
  /** Stepped forward. `charged` is true whenever the step left the sentence. */
  | { kind: 'step'; charged: boolean }
  /** Retraced one cell. Always free. */
  | { kind: 'back' }
  /** A wall, a cell that is not adjacent, or a shut gate. Nothing moved, nothing was charged. */
  | { kind: 'blocked' }
  /** Walked out through an open gate. */
  | { kind: 'cleared' };

const key = (c: Cell): string => `${c[0]},${c[1]}`;
const same = (a: Cell, b: Cell): boolean => a[0] === b[0] && a[1] === b[1];

/** The virtual cell the gate stands on, one column past the right-hand edge. */
export function gateCell(board: LabyrinthBoard): Cell {
  return [board.cols, board.gateRow];
}

export function startWalk(board: LabyrinthBoard, batteries: number): WalkState {
  return {
    trail: [board.path[0]],
    wrongTurns: 0,
    batteries,
    gateOpen: false,
    cleared: false,
  };
}

/** The words collected so far, in order — what the child sees written along the bottom. */
export function strip(board: LabyrinthBoard, state: WalkState): string[] {
  return state.trail.map(([c, r]) => board.grid[r][c]);
}

/**
 * Whether the gate stands open.
 *
 * The strip must read the sentence **exactly** — every word, in order, and nothing after it.
 * Reaching the exit is not winning, and neither is walking past the end of the sentence and back:
 * a strip holding the sentence plus one more word is not the sentence.
 */
export function gateIsOpen(board: LabyrinthBoard, state: WalkState): boolean {
  const read = strip(board, state);
  return read.length === board.sentence.length && read.every((w, i) => w === board.sentence[i]);
}

function adjacentForward(from: Cell, to: Cell): boolean {
  const dc = to[0] - from[0];
  const dr = to[1] - from[1];
  return (dc === 1 && dr === 0) || (dc === 0 && Math.abs(dr) === 1);
}

/**
 * Take one move. Returns the new state and what happened; the old state is never mutated.
 *
 * `to` is whatever cell the child touched — the caller does no filtering, because "that tap did
 * nothing" is a rule of the mode (there is no pathfinding and no auto-walk: the route is the answer)
 * rather than an input-layer concern.
 */
export function move(
  board: LabyrinthBoard,
  state: WalkState,
  to: Cell,
): { state: WalkState; outcome: MoveOutcome } {
  if (state.cleared) return { state, outcome: { kind: 'blocked' } };

  const head = state.trail[state.trail.length - 1];

  // Retracing. Tapping ANY cell already walked jumps straight back to it and drops everything after
  // it — not just the one step behind. A child four cells into a dead end can see the last word that
  // made sense and should be able to touch it, rather than tapping their way back through four
  // words they have already read and rejected. Always free: the charge is for leaving the sentence,
  // never for returning to it.
  const backTo = state.trail.findIndex((c) => same(c, to));
  if (backTo !== -1 && backTo < state.trail.length - 1) {
    const next = { ...state, trail: state.trail.slice(0, backTo + 1) };
    return { state: { ...next, gateOpen: gateIsOpen(board, next) }, outcome: { kind: 'back' } };
  }

  // Walking out. The gate is adjacent to the last column and opens only for the right reading.
  if (same(to, gateCell(board))) {
    if (state.gateOpen && head[0] === board.cols - 1 && head[1] === board.gateRow) {
      return { state: { ...state, cleared: true }, outcome: { kind: 'cleared' } };
    }
    return { state, outcome: { kind: 'blocked' } };
  }

  const inBounds = to[0] >= 0 && to[0] < board.cols && to[1] >= 0 && to[1] < board.rows;
  if (!inBounds) return { state, outcome: { kind: 'blocked' } };
  if (!adjacentForward(head, to)) return { state, outcome: { kind: 'blocked' } };
  if (!board.open.has(edgeKey(head, to))) return { state, outcome: { kind: 'blocked' } };
  if (state.trail.some((c) => same(c, to))) return { state, outcome: { kind: 'blocked' } };

  const onPath = new Set(board.path.map(key));
  const charge = onPath.has(key(head)) && !onPath.has(key(to));

  const next: WalkState = {
    ...state,
    trail: [...state.trail, to],
    wrongTurns: charge ? state.wrongTurns + 1 : state.wrongTurns,
    batteries: charge ? state.batteries - 1 : state.batteries,
  };
  return {
    state: { ...next, gateOpen: gateIsOpen(board, next) },
    outcome: { kind: 'step', charged: charge },
  };
}

/**
 * A perfect board: cleared without a single wrong turn charged.
 *
 * It buys nothing — batteries are one-way and nothing refunds them (§5.5) — but it is still the
 * honest answer to "was this board walked clean", which the scene reports and a later rung may use.
 */
export function wasPerfect(state: WalkState): boolean {
  return state.cleared && state.wrongTurns === 0;
}
